import React from 'react'
import styled from 'styled-components'
import { useStaticQuery, graphql, Link } from 'gatsby'
import { FaFacebookF } from '@react-icons/all-files/fa/FaFacebookF'
import { FaTwitter } from '@react-icons/all-files/fa/FaTwitter'
import { FaLinkedinIn } from '@react-icons/all-files/fa/FaLinkedinIn'
import { FaInstagram } from '@react-icons/all-files/fa/FaInstagram'
import { FaYoutube } from '@react-icons/all-files/fa/FaYoutube'
import { column } from 'styles/column'
import { clearfix } from 'styles/clearfix'
import { OutboundLink } from 'gatsby-plugin-google-gtag'

const Footer = () => {
  return (
    <StyledFooter>
      <LinksSection />
      <SocialSection />
      <PoliciesSection />
      <Copyright>Copyright &copy; 2018-Present Hutson Inc. All Rights Reserved.</Copyright>
    </StyledFooter>
  )
}

const StyledFooter = styled.footer`
  background: #000;
  bottom: 0;
  clear: both;
  color: #f0efef;
  width: 100%;

  @media print {
    display: none;
  }
`

const LinksSection = () => {
  return (
    <LinksSectionContainer>
      <Column>
        <Title>Products</Title>
        <LinkList>
          <li>
            <Link to='/lawn-garden/'>Lawn &amp; Garden Equipment</Link>
          </li>
          <li>
            <Link to='/agriculture/'>Agriculture Equipment</Link>
          </li>
          <li>
            <Link to='/construction/'>Construction Equipment</Link>
          </li>
          <li>
            <Link to='/implements/'>Implements</Link>
          </li>
          <li>
            <Link to='/product-lines/'>Product Lines</Link>
          </li>
          <li>
            <Link to='/stihl/'>STIHL</Link>
          </li>
          <li>
            <Link to='/government-sales/'>Government Sales</Link>
          </li>
        </LinkList>
      </Column>
      <Column>
        <Title>Used Equipment</Title>
        <LinkList>
          <li>
            <Link to='/used-equipment/tractors/compact-utility-tractors/'>
              Compact Utility Tractors
            </Link>
          </li>
          <li>
            <Link to='/used-equipment/tractors/utility-tractors/'>Utility Tractors</Link>
          </li>
          <li>
            <Link to='/used-equipment/lawn-mowers/lawn-and-garden-tractors/'>
              Lawn &amp; Garden Tractors
            </Link>
          </li>
          <li>
            <Link to='/used-equipment/lawn-mowers/zero-turn-mowers/'>Zero-Turn Mowers</Link>
          </li>
          <li>
            <Link to='/used-equipment/tractors/row-crop-tractors/'>Row Crop Tractors</Link>
          </li>
          <li>
            <Link to='/used-equipment/tractors/track-tractors/'>Track Tractors</Link>
          </li>
          <li>
            <Link to='/used-equipment/'>Search Used Equipment</Link>
          </li>
        </LinkList>
      </Column>
      <Column>
        <Title>Support</Title>
        <LinkList>
          <li>
            <Link to='/contact/'>Contact Us</Link>
          </li>
          <li>
            <Link to='/parts/'>Parts</Link>
          </li>
          <li>
            <Link to='/service/'>Service</Link>
          </li>
          <li>
            <Link to='/service/schedule-service/'>Schedule Service</Link>
          </li>
          <li>
            <Link to='/sentry/'>Sentry Insurance</Link>
          </li>
          <li>
            <Link to='/precision-ag/'>Precision Ag</Link>
          </li>
          <li>
            <Link to='/hutson-customer-portal/'>Hutson Customer Portal</Link>
          </li>
          <li>
            <Link to='/commitment-to-ownership-and-repair/'>
              Commitment to Ownership &amp; Repair
            </Link>
          </li>
        </LinkList>
      </Column>
      <Column>
        <Title>Other</Title>
        <LinkList>
          <li>
            <Link to='/locations/'>Locations</Link>
          </li>
          <li>
            <Link to='/about/'>About Us</Link>
          </li>
          <li>
            <Link to='/careers/'>Careers</Link>
          </li>
          <li>
            <Link to='/blog/'>Blog</Link>
          </li>
          <li>
            <Link to='/knowledge-center/'>Knowledge Center</Link>
          </li>
          <li>
            <Link to='/promotions/'>Current Promotions</Link>
          </li>
          <li>
            <Link to='/sponsorship/'>Sponsorship Request</Link>
          </li>
          <li>
            <Link to='/loaner/'>Loaner Request</Link>
          </li>
          <li>
            <Link to='/transfer/'>Dealer Transfer Request</Link>
          </li>
          <li>
            <OutboundLink href='https://form.jotform.com/211474123572046'>
              Customer Photo Submission
            </OutboundLink>
          </li>
        </LinkList>
      </Column>
    </LinksSectionContainer>
  )
}

const LinksSectionContainer = styled.div`
  margin: auto;
  width: 85%;

  a {
    color: #fff;
    font-size: 1.2em;
    text-decoration: none;

    :hover,
    :focus {
      outline-color: #fff;
      text-decoration: underline;
    }
  }

  @media (min-width: 800px) {
    ${clearfix}
    margin: auto;
    max-width: 1200px;
    width: 75%;

    a {
      font-size: 1em;
    }
  }
`

const Column = styled.div`
  padding-top: 40px;

  :last-of-type {
    padding-bottom: 40px;
  }

  @media (min-width: 800px) {
    ${column('1/4')}

    :last-of-type {
      padding-bottom: 0;
    }
  }
`

const Title = styled.h3`
  font-size: 1.3em;
  font-weight: bolder;
  text-transform: capitalize;

  @media (min-width: 800px) {
    font-size: 1em;
  }
`

const LinkList = styled.ul`
  font-size: 0.9em;
  list-style: none;
  padding-left: 0;
`

const SocialSection = () => {
  const {
    site: {
      siteMetadata: { facebookUrl, instagramUrl, linkedinUrl, twitterUrl, youtubeUrl },
    },
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          facebookUrl
          instagramUrl
          linkedinUrl
          twitterUrl
          youtubeUrl
        }
      }
    }
  `)
  return (
    <SocialContainer>
      <SocialLink
        href={facebookUrl}
        target='_blank'
        rel='noopener noreferrer'
        title='Like Hutson Inc on Facebook'
        aria-label='Like Hutson Inc on Facebook'
      >
        <FaFacebookF aria-hidden='true' focusable='false' role='presentation' />
      </SocialLink>
      <SocialLink
        href={twitterUrl}
        target='_blank'
        rel='noopener noreferrer'
        title='Follow Hutson Inc on Twitter'
        aria-label='Follow Hutson Inc on Twitter'
      >
        <FaTwitter aria-hidden='true' focusable='false' role='presentation' />
      </SocialLink>
      <SocialLink
        href={instagramUrl}
        target='_blank'
        rel='noopener noreferrer'
        title='Follow Hutson Inc on Instagram'
        aria-label='Follow Hutson Inc on Instagram'
      >
        <FaInstagram aria-hidden='true' focusable='false' role='presentation' />
      </SocialLink>
      <SocialLink
        href={linkedinUrl}
        target='_blank'
        rel='noopener noreferrer'
        title='Follow Hutson Inc on Linkedin'
        aria-label='Follow Hutson Inc on Linkedin'
      >
        <FaLinkedinIn aria-hidden='true' focusable='false' role='presentation' />
      </SocialLink>
      <SocialLink
        href={youtubeUrl}
        target='_blank'
        rel='noopener noreferrer'
        title='Subscribe to Hutson Inc on YouTube'
        aria-label='Subscribe to Hutson Inc on YouTube'
      >
        <FaYoutube aria-hidden='true' focusable='false' role='presentation' />
      </SocialLink>
    </SocialContainer>
  )
}

const SocialContainer = styled.div`
  clear: both;
  margin: 0 auto;
  padding-top: 20px;
  text-align: left;
  width: 85%;

  @media (min-width: 800px) {
    padding-top: 40px;
    text-align: center;
    width: 75%;
  }
`

const SocialLink = styled.a`
  color: #fff;
  display: inline-block;
  font-size: 1.5em;
  margin: 0 20px 0 0;
  opacity: 0.6;
  transition: opacity 0.1s ease;
  vertical-align: middle;

  :last-of-type {
    margin: 0;
  }

  :hover,
  :focus {
    opacity: 1;
    outline-color: #fff;
  }

  @media (min-width: 800px) {
    font-size: 1em;
    margin: 0;
    padding: 0 10px;
  }
`

const PoliciesSection = () => {
  return (
    <PoliciesSectionContainer>
      <ul>
        <li>
          <Link to='/privacy-policy/'>Privacy Policy</Link>
        </li>
        <li>
          <Link to='/terms-of-service/'>Terms of Service</Link>
        </li>
        <li>
          <OutboundLink
            href='https://forms.gle/NT64kPRSjPDNecnr5'
            target='_blank'
            rel='noopener noreferrer'
          >
            Mailing List Removal
          </OutboundLink>
        </li>
      </ul>
    </PoliciesSectionContainer>
  )
}

const PoliciesSectionContainer = styled.div`
  margin: 16px auto 0;
  text-align: left;
  width: 85%;

  ul {
    color: #909090;
    font-size: 0.9em;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: block;
      margin: 8px 0 0;
      padding: 0;
    }
  }

  a {
    color: #909090;
    text-decoration: none;

    :hover,
    :focus {
      outline-color: #fff;
      text-decoration: underline;
    }
  }

  @media (min-width: 800px) {
    text-align: center;
    width: 100%;

    ul {
      font-size: 0.8em;

      li {
        display: inline-block;

        :not(:last-child) {
          :after {
            content: '|';
            margin: 0 8px;
            display: inline-block;
          }
        }
      }
    }
  }
`

const Copyright = styled.p`
  color: #909090;
  font-size: 0.8em;
  margin: 16px auto 0;
  padding: 0 0 30px;
  text-align: left;
  width: 85%;

  @media (min-width: 800px) {
    font-size: 0.7em;
    text-align: center;
  }
`

const MemoizedFooter = React.memo(Footer)

export { MemoizedFooter as Footer }
